import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button, Switch, Table } from "@pentabd/ui";
import { IconPlus } from "@pentabd/icons";

import { ErrorModal, SuccessModal } from "@components";
import AddInstallmentModal from "./AddInstallmentModal";
import { TableColumn } from "./TableColumn";

import { completeInstallment } from "@api/gpf";

import { ROUTES } from "@constants/routes";
import { InstallmentDetailsTypes } from "@type/HRM/gpf";

interface InstallmentDetailsProps {
  id: string | undefined;
  loanID: string | undefined;
  installmentDetails: InstallmentDetailsTypes[];
  isFullyPaid: boolean;
}

const {
  HRM: { PROVIDENT_FUND },
} = ROUTES;

export const InstallmentDetailsTable = ({
  id,
  loanID,
  installmentDetails,
  isFullyPaid,
}: InstallmentDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isPaid, setIsPaid] = useState(isFullyPaid);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate(PROVIDENT_FUND);
  };
  const closeErrorModal = () => setIsErrorModalOpen(false);

  useEffect(() => {
    setIsPaid(isFullyPaid);
  }, [isFullyPaid]);

  const handleFullyPaid = (e: ChangeEvent<HTMLInputElement>) => {
    setIsPaid(e.target.checked);
    completeInstallment(id, loanID, e.target.checked, (response) => {
      if (response.status === "success") {
        const {
          data: { isFullyPaid },
        } = response.data;
        if (isFullyPaid) {
          setSuccessMessage("Loan paid successfully");
        } else {
          setSuccessMessage("Loan is set to unpaid");
        }
        setIsSuccessModalOpen(true);
      } else {
        setIsErrorModalOpen(true);
      }
    });
  };

  return (
    <div>
      {/* AFTER EFFECTS MODAL */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        subtitle={successMessage}
        action={closeSuccessModal}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
      {/* AFTER EFFECTS MODAL */}
      <Table
        header={{
          title: t("INSTALLMENT_DETAILS.TABLE.HEADER"),
          buttons: [
            <div className="d-flex align-items-center gap-10">
              <Switch
                id="installmentComplete"
                name="isFullyPaid"
                size="sm"
                checked={isPaid}
                onChange={handleFullyPaid}
                controlling
              />
              {!isPaid ? (
                <Button
                  size="sm"
                  fill="outline"
                  type="light"
                  onClick={openModal}
                >
                  <IconPlus size="20" />
                  {t("INSTALLMENT_DETAILS.TABLE.ADD_BUTTON_TEXT")}
                </Button>
              ) : null}
            </div>,
          ],
        }}
        columns={TableColumn(t)}
        rows={installmentDetails}
      />
      {/* MODAL */}
      <AddInstallmentModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        loanID={loanID as string}
        employeeID={id as string}
      />
    </div>
  );
};
